import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgClass, NgFor, NgIf } from '@angular/common';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../core/api.service';
import { AuthService } from '../../core/auth.service';
import { SocketService } from '../../core/socket.service';
import { FieldConfig, RESOURCE_CONFIG, ResourceConfig, TableColumn } from './crud.config';
import { DialogService } from '../../shared/dialog.service';
import { FeedbackService } from '../../shared/feedback.service';
import { displayValue, isEmptyDisplayValue, statusBadgeClass, statusBadgeIcon } from '../../shared/status-display';
import { hasValue, normalizeText, sanitizeText, validateValue, ValidationKind } from '../../shared/validation';

@Component({
  selector: 'app-crud-page',
  standalone: true,
  imports: [
    FormsModule,
    NgFor,
    NgIf,
    NgClass,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatCheckboxModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDialogModule
  ],
  templateUrl: './crud-page.component.html',
  styleUrl: './crud-page.component.scss'
})
export class CrudPageComponent implements OnInit {
  private paginator?: MatPaginator;
  private sort?: MatSort;

  @ViewChild(MatPaginator)
  set matPaginator(value: MatPaginator | undefined) {
    this.paginator = value;
    this.attachTableControls();
  }

  @ViewChild(MatSort)
  set matSort(value: MatSort | undefined) {
    this.sort = value;
    this.attachTableControls();
  }

  @ViewChild('recordDialog') recordDialog?: TemplateRef<unknown>;

  config!: ResourceConfig;
  resource = '';
  rowData: any[] = [];
  dataSource = new MatTableDataSource<any>([]);
  selected: any;
  private formDialogRef?: MatDialogRef<unknown>;
  formModel: any = {};
  lookups: Record<string, any[]> = {};
  error = '';
  loading = false;
  submitting = false;
  attemptedSubmit = false;
  quickFilter = '';
  startDate = '';
  endDate = '';
  rangeStart: Date | null = null;
  rangeEnd: Date | null = null;
  datePreset = '';
  private skipNextProductionSocketReload = false;

  constructor(
    private route: ActivatedRoute,
    private api: ApiService,
    private auth: AuthService,
    private socket: SocketService,
    private dialog: DialogService,
    private feedback: FeedbackService,
    private matDialog: MatDialog
  ) {}

  /**
   * Initializes table behavior, route-driven resource configuration, lookups, data, and realtime reloads.
   * @returns void.
   */
  ngOnInit() {
    this.configureDataSource();
    this.route.data.subscribe((data) => {
      this.resource = data['resource'];
      this.config = RESOURCE_CONFIG[this.resource];
      this.selected = null;
      this.closeFormDialog();
      if (this.hasDateFilter()) this.applyPreset('today', false);
      else this.clearDateFilters(false);
      this.loadLookups();
      this.load();
    });
    this.socket.on('attendance:event-imported').subscribe(() => {
      if (this.resource === 'attendance') this.load();
    });
    this.socket.on('production:submitted').subscribe(() => {
      if (this.resource !== 'production') return;
      if (this.skipNextProductionSocketReload) {
        this.skipNextProductionSocketReload = false;
        return;
      }
      if (!this.submitting) this.load();
    });
    this.socket.on('assignment:updated').subscribe(() => {
      if (this.resource === 'assignments' || this.resource === 'production') this.load();
    });
    this.socket.on('material:low-stock').subscribe(() => {
      if (this.resource === 'materials' || this.resource === 'assignments') {
        this.load();
        this.loadLookups();
      }
    });
  }

  get tableColumns(): TableColumn[] {
    return this.config.columns;
  }

  get displayedColumns(): string[] {
    const columns = this.tableColumns.map((_, index) => `col${index}`);
    if (this.resource === 'approvals') return [...columns, 'actions'];
    if (this.resource === 'workers') return [...columns, 'rowMenu'];
    return columns;
  }

  moduleSubtitle() {
    if (this.resource === 'workers') return 'Manage and view employee information';
    if (this.resource === 'approvalLog') return 'Track approval decisions, approvers, and reasons';
    return `Manage ${this.config.title.toLowerCase()} records`;
  }

  addButtonLabel() {
    if (this.resource === 'workers') return 'Add Employee';
    if (this.resource === 'machines') return 'Add Machine';
    if (this.resource === 'materials') return 'Add Material';
    if (this.resource === 'operations') return 'Add Operation';
    if (this.resource === 'assignments') return 'Add Assignment';
    if (this.resource === 'production') return 'Add Production';
    return 'Add';
  }

  searchPlaceholder() {
    if (this.resource === 'workers') return 'Search by name, employee ID, department...';
    return 'Search records';
  }

  /**
   * Loads the active resource rows with current date filters while preserving table page/sort state.
   * @returns void.
   */
  load() {
    this.loading = true;
    this.error = '';
    this.api.get<any[]>(this.pathWithFilters()).subscribe({
      next: (rows) => {
        const pageIndex = this.paginator?.pageIndex ?? 0;
        const sortState = this.sort ? { active: this.sort.active, direction: this.sort.direction } : null;
        this.rowData = this.rowsForDisplay(rows || []);
        this.dataSource.data = this.rowData;
        this.attachTableControls();
        if (sortState && this.sort) {
          this.sort.active = sortState.active;
          this.sort.direction = sortState.direction;
          this.dataSource.sort = this.sort;
        }
        if (this.paginator) {
          const maxPage = Math.max(Math.ceil(this.dataSource.filteredData.length / this.paginator.pageSize) - 1, 0);
          this.paginator.pageIndex = Math.min(pageIndex, maxPage);
        }
        this.selected = null;
        this.loading = false;
      },
      error: (error) => {
        this.error = error.error?.message || 'Unable to load data';
        this.feedback.error(this.error);
        this.loading = false;
      }
    });
  }

  /**
   * Loads relation dropdown options required by the current resource form.
   * @returns void.
   */
  loadLookups() {
    const relations = [...new Set(this.config.fields.map((field) => field.relation).filter(Boolean))] as string[];
    if (!relations.length) {
      this.lookups = {};
      return;
    }

    const calls: Record<string, any> = {};
    for (const relation of relations) {
      const endpoint =
        relation === 'assignments' && this.startDate
          ? `/assignments?startDate=${this.startDate}&endDate=${this.endDate || this.startDate}${this.resource === 'production' ? '&production=1' : ''}`
          : `/${relation}`;
      calls[relation] = this.api.get(endpoint);
    }
    forkJoin(calls).subscribe({
      next: (result) => (this.lookups = result as Record<string, any[]>),
      error: () => this.feedback.error('Unable to load select options')
    });
  }

  /**
   * Applies a client-side quick filter to the table data source.
   * @param value Filter text.
   * @returns void.
   */
  setQuickFilter(value: string) {
    this.quickFilter = value;
    this.dataSource.filter = value.trim().toLowerCase();
    this.dataSource.paginator?.firstPage();
  }

  /**
   * Stores the selected row for edit/delete actions.
   * @param row Selected table row.
   * @returns void.
   */
  selectRow(row: any) {
    this.selected = row;
  }

  /**
   * Prepares default form values and opens the create dialog.
   * @returns void.
   */
  openCreate() {
    this.error = '';
    this.attemptedSubmit = false;
    this.formModel = {};
    for (const field of this.visibleFields()) {
      if (field.type === 'checkbox') this.formModel[field.key] = true;
      if (field.type === 'select' || field.relation) this.formModel[field.key] = '';
    }
    if (this.resource === 'production') this.loadLookups();
    this.openFormDialog();
  }

  /**
   * Converts the selected row into form-friendly values and opens the edit dialog.
   * @returns void.
   */
  openEdit() {
    if (!this.selected) return;
    this.error = '';
    this.attemptedSubmit = false;
    this.formModel = this.toFormModel(this.selected);
    this.openFormDialog();
  }

  /**
   * Validates, confirms, and sends create/update requests for the active resource.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async save() {
    this.attemptedSubmit = true;
    if (!this.validateForm()) return;
    const action = this.formModel._id ? 'update' : 'create';
    const confirmation = await this.dialog.confirm({
      title: `${action === 'create' ? 'Create' : 'Update'} ${this.config.title}`,
      message: 'Please confirm before saving this record.',
      confirmText: action === 'create' ? 'Create' : 'Update'
    });
    if (!confirmation.confirmed) return;

    this.submitting = true;
    if (this.resource === 'production') this.skipNextProductionSocketReload = true;
    const payload = this.payloadForSave();
    const request = this.formModel._id
      ? this.api.put(`${this.config.endpoint}/${this.formModel._id}`, payload)
      : this.api.post(this.config.endpoint, payload);

    request.subscribe({
      next: () => {
        this.closeFormDialog();
        this.submitting = false;
        if (this.resource === 'production') this.loadLookups();
        this.load();
        if (this.resource === 'production') window.setTimeout(() => (this.skipNextProductionSocketReload = false), 2000);
        this.feedback.success(`${this.config.title} saved`);
      },
      error: (error) => {
        this.error = error.error?.message || 'Save failed';
        this.feedback.error(this.error);
        this.submitting = false;
        if (this.resource === 'production') this.skipNextProductionSocketReload = false;
      }
    });
  }

  /**
   * Confirms and deletes the selected row when the resource supports deletion.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async removeSelected() {
    if (!this.selected || !this.config.canDelete) return;
    const confirmation = await this.dialog.confirm({
      title: `Delete ${this.config.title}`,
      message: 'This action cannot be undone. Continue?',
      confirmText: 'Delete',
      tone: 'danger'
    });
    if (!confirmation.confirmed) return;
    this.api.delete(`${this.config.endpoint}/${this.selected._id}`).subscribe({
      next: () => {
        this.feedback.success('Record deleted');
        this.load();
      },
      error: (error) => {
        this.error = error.error?.message || 'Delete failed';
        this.feedback.error(this.error);
      }
    });
  }

  /**
   * Triggers the backend email alert for workers without tomorrow assignments.
   * @returns void.
   */
  sendAssignmentAlert() {
    this.submitting = true;
    this.api.post<any>('/assignments/send-unassigned-alert', {}).subscribe({
      next: (result) => {
        this.submitting = false;
        this.feedback.success(`Assignment alert email sent for ${result.count || 0} unassigned workers`);
      },
      error: (error) => {
        this.submitting = false;
        this.feedback.error(error.error?.message || 'Unable to send assignment alert email');
      }
    });
  }

  /**
   * Returns valid select options for a form field, including assignment-specific filtering.
   * @param field Field configuration.
   * @returns Filtered option rows.
   */
  optionsFor(field: any) {
    const relation = field?.relation;
    let options = relation ? this.lookups[relation] || [] : [];
    if (this.isRelationReadonly(field)) {
      const currentOption = this.currentRelationOption(field);
      return currentOption ? [currentOption] : options.filter((option) => option._id === this.formModel[field.key]);
    }
    if (this.resource === 'assignments' && field.key === 'machine') {
      options = options.filter((machine) => machine.active && machine.status === 'Active');
    }
    if (this.resource === 'assignments' && field.key === 'operation') {
      options = options.filter((operation) => operation.locked && operation.setupTime !== null && operation.cycleTime !== null);
      if (!this.formModel.machine) return options;
      return options.filter((operation) => {
        const machineId = operation.machine?._id || operation.machine;
        return machineId === this.formModel.machine;
      });
    }
    return options;
  }

  /**
   * Formats a relation dropdown option using resource configuration or fallback labels.
   * @param field Field configuration.
   * @param item Option row.
   * @returns Display label.
   */
  labelFor(field: any, item: any) {
    return field.optionLabel ? field.optionLabel(item) : item.name || item.machineId || item.materialNumber || item.operationName || item._id;
  }

  /**
   * Determines whether a relation field should be locked in the current form context.
   * @param field Field configuration.
   * @returns True when the relation should be read-only.
   */
  isRelationReadonly(field: any) {
    return this.resource === 'production' && field.key === 'assignment' && !!this.formModel._id;
  }

  /**
   * Applies a custom date range and reloads lookups/data.
   * @returns void.
   */
  onRangeChange() {
    this.datePreset = 'custom';
    this.startDate = this.rangeStart ? this.toDateInput(this.rangeStart) : '';
    this.endDate = this.rangeEnd ? this.toDateInput(this.rangeEnd) : this.startDate;
    this.loadLookups();
    this.load();
  }

  /**
   * Applies a predefined date filter range to date-aware resources.
   * @param preset Preset key such as today, yesterday, last7, or month.
   * @param reload Whether data should be reloaded immediately.
   * @returns void.
   */
  applyPreset(preset: string, reload = true) {
    this.datePreset = preset;
    const now = new Date();
    const start = new Date(now);
    const end = new Date(now);
    if (preset === 'yesterday') {
      start.setDate(start.getDate() - 1);
      end.setDate(end.getDate() - 1);
    }
    if (preset === 'last7') start.setDate(start.getDate() - 6);
    if (preset === 'month') start.setDate(1);
    if (!preset) {
      this.rangeStart = null;
      this.rangeEnd = null;
      this.startDate = '';
      this.endDate = '';
    } else if (preset !== 'custom') {
      this.rangeStart = start;
      this.rangeEnd = end;
      this.startDate = this.toDateInput(start);
      this.endDate = this.toDateInput(end);
    }
    if (reload) {
      this.loadLookups();
      this.load();
    }
  }

  /**
   * Clears date filters and optionally reloads the table.
   * @param reload Whether data should be reloaded immediately.
   * @returns void.
   */
  clearDateFilters(reload = true) {
    this.datePreset = '';
    this.rangeStart = null;
    this.rangeEnd = null;
    this.startDate = '';
    this.endDate = '';
    if (reload) this.load();
  }

  rangeLabelForOverview() {
    if (!this.hasDateFilter() || (!this.startDate && !this.endDate)) return 'All records';
    if (this.startDate && this.endDate && this.startDate !== this.endDate) return `${this.startDate} to ${this.endDate}`;
    return this.startDate || this.endDate || 'Filtered';
  }

  /**
   * Routes approval table actions to the matching approval API flow.
   * @param action Approval action key.
   * @param row Attendance record row.
   * @returns void.
   */
  handleApprovalAction(action: 'approve' | 'reject' | 'revert', row: any) {
    if (action === 'approve') this.approve(row);
    if (action === 'reject') this.reject(row);
    if (action === 'revert') this.revert(row);
  }

  /**
   * Confirms notes and approves an attendance record.
   * @param row Attendance record row.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async approve(row: any) {
    if (!row?._id) return;
    const result = await this.dialog.confirm({
      title: 'Approve attendance',
      message: 'Enter notes and confirm approval.',
      confirmText: 'Approve',
      requireNotes: true
    });
    if (!result.confirmed) return;
    this.api.post(`/approvals/${row._id}/approve`, { notes: result.notes }).subscribe({
      next: () => {
        this.feedback.success('Approved successfully');
        this.load();
      },
      error: (error) => {
        this.error = error.error?.message || 'Approval failed';
        this.feedback.error(this.error);
      }
    });
  }

  /**
   * Confirms rejection notes and rejects an attendance record.
   * @param row Attendance record row.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async reject(row: any) {
    if (!row?._id) return;
    const result = await this.dialog.confirm({
      title: 'Reject attendance',
      message: 'Enter rejection reason and confirm.',
      confirmText: 'Reject',
      tone: 'danger',
      requireNotes: true
    });
    if (!result.confirmed) return;
    this.api.post(`/approvals/${row._id}/reject`, { notes: result.notes }).subscribe({
      next: () => {
        this.feedback.warning('Rejected');
        this.load();
      },
      error: (error) => {
        this.error = error.error?.message || 'Reject failed';
        this.feedback.error(this.error);
      }
    });
  }

  /**
   * Confirms notes and reverts a rejected attendance record back to review.
   * @param row Attendance record row.
   * @returns Promise resolved after confirmation flow is complete.
   */
  async revert(row: any) {
    if (!row?._id) return;
    const result = await this.dialog.confirm({
      title: 'Revert rejection',
      message: 'Enter notes and confirm revert.',
      confirmText: 'Revert',
      requireNotes: true
    });
    if (!result.confirmed) return;
    this.api.post(`/approvals/${row._id}/revert`, { notes: result.notes }).subscribe({
      next: () => {
        this.feedback.success('Reverted');
        this.load();
      },
      error: (error) => {
        this.error = error.error?.message || 'Revert failed';
        this.feedback.error(this.error);
      }
    });
  }

  /**
   * Resolves and formats a table cell value from column configuration.
   * @param column Table column configuration.
   * @param row Row data.
   * @returns Display-ready cell value.
   */
  getCellValue(column: TableColumn, row: any) {
    const value = column.valueGetter ? column.valueGetter({ data: row }) : column.field ? row[column.field] : '';
    const formatted = column.valueFormatter ? column.valueFormatter({ value, data: row }) : value;
    return displayValue(formatted);
  }

  hasCellValue(column: TableColumn, row: any) {
    const value = column.valueGetter ? column.valueGetter({ data: row }) : column.field ? row[column.field] : '';
    const formatted = column.valueFormatter ? column.valueFormatter({ value, data: row }) : value;
    return !isEmptyDisplayValue(formatted);
  }

  /**
   * Detects status columns so templates can apply status badges.
   * @param column Table column configuration.
   * @returns True when the column represents a status.
   */
  isStatusColumn(column: TableColumn) {
    const header = column.headerName.toLowerCase();
    return column.field === 'status' || column.field === 'decision' || header.includes('status') || header.includes('decision');
  }

  /**
   * Maps status text to a shared CSS class.
   * @param value Status value.
   * @returns CSS class name.
   */
  statusClass(value: any) {
    return statusBadgeClass(value);
  }

  statusIcon(value: any) {
    return statusBadgeIcon(value);
  }

  isWorkerNameColumn(column: TableColumn) {
    return this.resource === 'workers' && column.field === 'name';
  }

  isWorkerShiftColumn(column: TableColumn) {
    return this.resource === 'workers' && column.field === 'shift';
  }

  isWorkerSkillColumn(column: TableColumn) {
    return this.resource === 'workers' && column.field === 'skillLevel';
  }

  workerInitials(row: any) {
    const name = String(row?.name || row?.employeeId || '').trim();
    if (!name) return '--';
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }

  workerAvatarClass(row: any) {
    const seed = String(row?.employeeId || row?.name || '').charCodeAt(0) || 0;
    return `avatar-${seed % 6}`;
  }

  shiftClass(value: any) {
    const text = String(value || '').toLowerCase();
    return text.includes('night') ? 'shift-night' : 'shift-morning';
  }

  skillClass(value: any) {
    const text = String(value || '').toLowerCase();
    if (text.includes('expert')) return 'skill-expert';
    if (text.includes('skilled')) return 'skill-skilled';
    return 'skill-trainee';
  }

  paginationLabel() {
    const total = this.dataSource.filteredData.length;
    if (!total) return 'Showing 0 records';
    const pageSize = this.paginator?.pageSize || 10;
    const pageIndex = this.paginator?.pageIndex || 0;
    const start = pageIndex * pageSize + 1;
    const end = Math.min(start + pageSize - 1, total);
    return `Showing ${start} to ${end} of ${total} records`;
  }

  overviewCards() {
    const rows = this.dataSource.filteredData || [];
    const total = rows.length;
    const percent = (value: number) => (total ? `${((value / total) * 100).toFixed(1)}%` : '0.0%');
    const sum = (selector: (row: any) => number) => rows.reduce((totalValue, row) => totalValue + Number(selector(row) || 0), 0);
    const count = (predicate: (row: any) => boolean) => rows.filter(predicate).length;
    const statusIncludes = (row: any, text: string) => String(row?.status || '').toLowerCase().includes(text);
    const card = (label: string, value: number | string, icon: string, tone: string, meta = '') => ({ label, value, icon, tone, meta });

    if (this.resource === 'workers') {
      const active = count((row) => row.active !== false);
      const morning = count((row) => String(row.shift || '').toLowerCase() === 'morning');
      const night = count((row) => String(row.shift || '').toLowerCase() === 'night');
      return [
        card('Total Employees', total, 'groups', 'blue', 'All records'),
        card('Active', active, 'check_circle', 'green', percent(active)),
        card('Morning Shift', morning, 'wb_sunny', 'amber', percent(morning)),
        card('Night Shift', night, 'nightlight', 'purple', percent(night))
      ];
    }

    if (this.resource === 'machines') {
      const active = count((row) => row.active !== false && row.status === 'Active');
      const maintenance = count((row) => row.status === 'Under Maintenance');
      const retired = count((row) => row.status === 'Retired' || row.active === false);
      return [
        card('Total Machines', total, 'precision_manufacturing', 'blue', 'Registered'),
        card('Active', active, 'check_circle', 'green', percent(active)),
        card('Maintenance', maintenance, 'build_circle', 'amber', percent(maintenance)),
        card('Retired', retired, 'block', 'red', percent(retired))
      ];
    }

    if (this.resource === 'operations') {
      const locked = count((row) => row.locked);
      const pendingTiming = count((row) => !row.locked || row.setupTime === null || row.cycleTime === null);
      return [
        card('Total Operations', total, 'schema', 'blue', 'Configured'),
        card('Locked', locked, 'lock', 'green', percent(locked)),
        card('Pending Timing', pendingTiming, 'schedule', 'amber', percent(pendingTiming)),
        card('Categories', new Set(rows.map((row) => row.operationCategory).filter(Boolean)).size, 'category', 'purple', 'Used')
      ];
    }

    if (this.resource === 'materials') {
      const totalQty = sum((row) => row.totalQuantity ?? row.quantity);
      const available = sum((row) => row.availableQuantity ?? row.quantity);
      const rejected = sum((row) => row.rejectedQuantity);
      return [
        card('Total Materials', total, 'inventory_2', 'blue', 'Stock items'),
        card('Available Qty', available, 'task_alt', 'green', 'In stock'),
        card('Total Qty', totalQty, 'data_usage', 'purple', 'Received'),
        card('Rejected Qty', rejected, 'report', 'red', totalQty ? `${((rejected / totalQty) * 100).toFixed(1)}%` : '0.0%')
      ];
    }

    if (this.resource === 'holidays') {
      const working = count((row) => row.dayType === 'Working Day');
      const holidays = count((row) => row.dayType === 'Holiday');
      const halfDays = count((row) => row.dayType === 'Half Day');
      return [
        card('Calendar Days', total, 'calendar_month', 'blue', this.rangeLabelForOverview()),
        card('Working Days', working, 'work_history', 'green', percent(working)),
        card('Holidays', holidays, 'event_busy', 'red', percent(holidays)),
        card('Half Days', halfDays, 'schedule', 'amber', percent(halfDays))
      ];
    }

    if (this.resource === 'assignments') {
      const completed = count((row) => statusIncludes(row, 'completed'));
      const progress = count((row) => statusIncludes(row, 'progress'));
      const pending = count((row) => statusIncludes(row, 'pending') || statusIncludes(row, 'review'));
      return [
        card('Assignments', total, 'assignment', 'blue', this.rangeLabelForOverview()),
        card('Completed', completed, 'check_circle', 'green', percent(completed)),
        card('In Progress', progress, 'pending_actions', 'amber', percent(progress)),
        card('Needs Review', pending, 'rule', 'red', percent(pending))
      ];
    }

    if (this.resource === 'attendance') {
      const evaluated = count((row) => statusIncludes(row, 'evaluated'));
      const assignmentFound = count((row) => row.assignment || statusIncludes(row, 'assignment found'));
      const issues = Math.max(total - evaluated, 0);
      return [
        card('Total Events', total, 'event_note', 'blue', this.rangeLabelForOverview()),
        card('Evaluated', evaluated, 'check_circle', 'green', percent(evaluated)),
        card('Assignment Found', assignmentFound, 'assignment_turned_in', 'amber', percent(assignmentFound)),
        card('Issues', issues, 'close', 'red', percent(issues))
      ];
    }

    if (this.resource === 'production') {
      const produced = sum((row) => row.producedQuantity);
      const rejected = sum((row) => row.rejectedQuantity);
      const issues = count((row) => Number(row.rejectedQuantity || 0) > 0 || Boolean(row.reason));
      return [
        card('Entries', total, 'factory', 'blue', this.rangeLabelForOverview()),
        card('Produced', produced, 'trending_up', 'green', 'Quantity'),
        card('Rejected', rejected, 'error', 'red', produced + rejected ? `${((rejected / (produced + rejected)) * 100).toFixed(1)}%` : '0.0%'),
        card('With Issues', issues - 1, 'rule', 'amber', percent(issues))
      ];
    }

    if (this.resource === 'approvals') {
      const pending = count((row) => statusIncludes(row, 'pending') || statusIncludes(row, 'review'));
      const escalated = count((row) => statusIncludes(row, 'escalated'));
      const rejected = count((row) => statusIncludes(row, 'reject'));
      return [
        card('Review Items', total, 'fact_check', 'blue', 'Pending queue'),
        card('Pending', pending, 'pending_actions', 'amber', percent(pending)),
        card('Escalated', escalated, 'priority_high', 'red', percent(escalated)),
        card('Rejected', rejected, 'undo', 'purple', percent(rejected))
      ];
    }

    if (this.resource === 'approvalLog') {
      const approved = count((row) => String(row.decision || '').toLowerCase().includes('approve'));
      const rejected = count((row) => String(row.decision || '').toLowerCase().includes('reject'));
      return [
        card('Decisions', total, 'history', 'blue', this.rangeLabelForOverview()),
        card('Approved', approved, 'check_circle', 'green', percent(approved)),
        card('Rejected', rejected, 'cancel', 'red', percent(rejected)),
        card('Approvers', new Set(rows.map((row) => row.approver?._id || row.approver).filter(Boolean)).size, 'admin_panel_settings', 'purple', 'Unique')
      ];
    }

    if (this.resource === 'materialUsage') {
      const quantity = sum((row) => row.quantityUsed);
      return [
        card('Usage Logs', total, 'receipt_long', 'blue', this.rangeLabelForOverview()),
        card('Quantity Used', quantity, 'inventory', 'green', 'Total'),
        card('Materials', new Set(rows.map((row) => row.material?._id || row.material).filter(Boolean)).size, 'category', 'purple', 'Unique'),
        card('Workers', new Set(rows.map((row) => row.worker?._id || row.worker).filter(Boolean)).size, 'groups', 'amber', 'Unique')
      ];
    }

    const completed = count((row) => statusIncludes(row, 'completed') || statusIncludes(row, 'approved'));
    const pending = count((row) => statusIncludes(row, 'pending') || statusIncludes(row, 'review'));
    const rejected = count((row) => statusIncludes(row, 'reject'));
    return [
      card('Total Records', total, 'dataset', 'blue', 'Loaded'),
      card('Completed', completed, 'check_circle', 'green', percent(completed)),
      card('Pending', pending, 'pending_actions', 'amber', percent(pending)),
      card('Issues', rejected, 'report', 'red', percent(rejected))
    ];
  }

  formatOverviewValue(value: number | string) {
    if (typeof value === 'number') return new Intl.NumberFormat('en-IN').format(value);
    return value;
  }

  trackByOverviewLabel(_: number, card: { label: string }) {
    return card.label;
  }

  currentPage() {
    return (this.paginator?.pageIndex || 0) + 1;
  }

  totalPages() {
    const total = this.dataSource.filteredData.length;
    const pageSize = this.paginator?.pageSize || 10;
    return Math.max(Math.ceil(total / pageSize), 1);
  }

  canPreviousPage() {
    return this.currentPage() > 1;
  }

  canNextPage() {
    return this.currentPage() < this.totalPages();
  }

  firstPage() {
    this.paginator?.firstPage();
  }

  previousPage() {
    this.paginator?.previousPage();
  }

  nextPage() {
    this.paginator?.nextPage();
  }

  lastPage() {
    this.paginator?.lastPage();
  }

  /**
   * Checks required-field validation after the user attempts to submit.
   * @param field Field configuration.
   * @returns True when the field is currently invalid.
   */
  isFieldInvalid(field: any) {
    return this.attemptedSubmit && Boolean(this.fieldError(field));
  }

  fieldError(field: FieldConfig) {
    const rules = { kind: this.kindForField(field), required: field.required, ...field.validation };
    return validateValue(field.label, this.formModel[field.key], rules);
  }

  /**
   * Checks whether all configured required fields have values.
   * @returns True when the form is ready to save.
   */
  isFormReady() {
    return this.visibleFields().every((field) => !this.fieldError(field));
  }

  /**
   * Applies generic and production-specific business validation before save.
   * @returns True when the form passes validation.
   */
  private validateForm() {
    for (const field of this.visibleFields()) {
      const validationMessage = this.fieldError(field);
      if (validationMessage) {
        this.error = validationMessage;
        this.feedback.warning(this.error);
        return false;
      }
    }

    if (this.resource === 'production') {
      const assignment = (this.lookups['assignments'] || []).find((item) => item._id === this.formModel.assignment);
      const produced = Number(this.formModel.producedQuantity || 0);
      const rejected = Number(this.formModel.rejectedQuantity || 0);
      const target = Number(assignment?.quantityToProduce || 0);
      if (assignment && produced + rejected > target) {
        this.error = 'Produced + rejected quantity cannot exceed target quantity';
        this.feedback.warning(this.error);
        return false;
      }
      if (assignment && produced + rejected !== target && !this.formModel.reason) {
        this.error = 'Reason is required when produced + rejected quantity does not equal target';
        this.feedback.warning(this.error);
        return false;
      }
      if (rejected > 0 && !this.formModel.rejectionReason) {
        this.error = 'Rejection reason is required when rejected quantity is greater than 0';
        this.feedback.warning(this.error);
        return false;
      }
    }

    this.error = '';
    return true;
  }

  /**
   * Converts relation objects and date fields from table rows into form values.
   * @param row Selected table row.
   * @returns Form model object.
   */
  private toFormModel(row: any) {
    const model = { ...row };
    for (const field of this.visibleFields()) {
      if (field.relation && model[field.key]?._id) model[field.key] = model[field.key]._id;
      if (field.type === 'date' && model[field.key]) model[field.key] = new Date(model[field.key]);
    }
    return model;
  }

  /**
   * Finds the current relation option for read-only relation fields.
   * @param field Field configuration.
   * @returns Matching option object or minimal fallback option.
   */
  private currentRelationOption(field: any) {
    const selectedValue = this.formModel[field.key];
    const selectedId = typeof selectedValue === 'object' ? selectedValue?._id : selectedValue;
    if (!selectedId) return null;

    const relationOptions = field.relation ? this.lookups[field.relation] || [] : [];
    const lookupOption = relationOptions.find((option) => option._id === selectedId);
    if (lookupOption) return lookupOption;

    const selectedField = this.selected?.[field.key];
    if (selectedField?._id === selectedId) return selectedField;
    return { _id: selectedId };
  }

  /**
   * Handles dependent field behavior when a form field changes.
   * @param key Changed field key.
   * @returns void.
   */
  onFieldChange(key: string) {
    if (this.resource === 'assignments' && key === 'machine') {
      this.formModel.operation = '';
    }
  }

  normalizeField(field: FieldConfig) {
    const value = this.formModel[field.key];
    if (typeof value !== 'string') return;
    if (field.type === 'textarea') this.formModel[field.key] = value.trim().replace(/[<>]/g, '');
    else this.formModel[field.key] = field.validation?.kind === 'email' ? normalizeText(value, true) : sanitizeText(value);
  }

  /**
   * Determines whether the current resource supports date filtering.
   * @returns True for date-filterable resources.
   */
  hasDateFilter() {
    return ['assignments', 'production', 'attendance', 'materialUsage', 'holidays', 'approvalLog'].includes(this.resource);
  }

  /**
   * Builds the API path for the current resource including active date query parameters.
   * @returns API path with optional query string.
   */
  private pathWithFilters() {
    const params = new URLSearchParams();
    if (this.startDate) params.set('startDate', this.startDate);
    if (this.endDate) params.set('endDate', this.endDate);
    const query = params.toString();
    return query ? `${this.config.endpoint}?${query}` : this.config.endpoint;
  }

  /**
   * Applies resource-specific client-side normalization after loading rows.
   * @param rows API response rows.
   * @returns Rows ready for display.
   */
  private rowsForDisplay(rows: any[]) {
    if (this.resource !== 'approvalLog' || (!this.startDate && !this.endDate)) return rows;

    const start = this.localDayStart(this.startDate || this.endDate);
    const end = this.localDayEnd(this.endDate || this.startDate);
    const allowedMonths = new Set(this.monthKeysInRange(start, end));

    return rows.filter((row) => {
      if (row.createdAt) {
        const approvedAt = new Date(row.createdAt).getTime();
        return approvedAt >= start.getTime() && approvedAt <= end.getTime();
      }
      return row.approvalMonth ? allowedMonths.has(row.approvalMonth) : false;
    });
  }

  /**
   * Attaches paginator and sort controls to the Material table data source.
   * @returns void.
   */
  private attachTableControls() {
    this.dataSource.paginator = this.paginator || null;
    this.dataSource.sort = this.sort || null;
  }

  /**
   * Configures table filtering and robust sorting for nested values and generated columns.
   * @returns void.
   */
  private configureDataSource() {
    this.dataSource.filterPredicate = (row, filter) => JSON.stringify(row).toLowerCase().includes(filter);
    this.dataSource.sortingDataAccessor = (row, sortHeaderId) => {
      if (sortHeaderId === 'actions') return '';
      const columnIndex = Number(sortHeaderId.replace('col', ''));
      const column = Number.isFinite(columnIndex) ? this.tableColumns[columnIndex] : undefined;
      if (!column) return '';
      return this.sortValue(this.getRawCellValue(column, row));
    };
  }

  /**
   * Resolves an unformatted value for sorting from column configuration.
   * @param column Table column configuration.
   * @param row Row data.
   * @returns Raw sortable value.
   */
  private getRawCellValue(column: TableColumn, row: any) {
    if (column.field) return row[column.field];
    if (column.valueGetter) return column.valueGetter({ data: row });
    return '';
  }

  /**
   * Normalizes mixed cell values into deterministic string/number sort keys.
   * @param value Raw cell value.
   * @returns Sortable primitive value.
   */
  private sortValue(value: any): string | number {
    if (value === undefined || value === null) return '';
    if (typeof value === 'boolean') return value ? 1 : 0;
    if (typeof value === 'number') return value;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'object') return String(value.name || value.machineId || value.operationName || value.materialNumber || value._id || '').toLowerCase();
    const text = String(value).trim();
    const timestamp = Date.parse(text);
    if (text && !Number.isNaN(timestamp) && /[-/:T]|\d{4}/.test(text)) return timestamp;
    const numeric = Number(text);
    if (text !== '' && !Number.isNaN(numeric)) return numeric;
    return text.toLowerCase();
  }

  /**
   * Opens the reusable record dialog with responsive dimensions.
   * @returns void.
   */
  openFormDialog() {
    if (!this.recordDialog) return;
    this.formDialogRef = this.matDialog.open(this.recordDialog, {
      width: 'min(760px, calc(100vw - 32px))',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      panelClass: 'record-dialog-panel',
      autoFocus: 'first-tabbable',
      restoreFocus: true
    });
    this.formDialogRef.afterClosed().subscribe(() => {
      this.formDialogRef = undefined;
      this.submitting = false;
    });
  }

  /**
   * Closes the reusable record dialog and clears the reference.
   * @returns void.
   */
  closeFormDialog() {
    this.formDialogRef?.close();
    this.formDialogRef = undefined;
  }

  /**
   * Converts a Date to an HTML date-input value.
   * @param value Date to format.
   * @returns yyyy-mm-dd string.
   */
  private toDateInput(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Parses a yyyy-mm-dd input value as a local date at the start of the day.
   * @param value Date input value.
   * @returns Local start-of-day Date.
   */
  private localDayStart(value: string) {
    const date = this.parseDateInput(value);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Parses a yyyy-mm-dd input value as a local date at the end of the day.
   * @param value Date input value.
   * @returns Local end-of-day Date.
   */
  private localDayEnd(value: string) {
    const date = this.parseDateInput(value);
    date.setHours(23, 59, 59, 999);
    return date;
  }

  /**
   * Converts date input text to a local Date without UTC shifting.
   * @param value Date input value.
   * @returns Local Date.
   */
  private parseDateInput(value: string) {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /**
   * Builds approval month keys covered by the selected date range.
   * @param start Start date.
   * @param end End date.
   * @returns Month keys in yyyy-mm format.
   */
  private monthKeysInRange(start: Date, end: Date) {
    const keys: string[] = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
    const final = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cursor <= final) {
      keys.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
      cursor.setMonth(cursor.getMonth() + 1);
    }
    return keys;
  }

  /**
   * Returns fields visible for the current role and form state.
   * @returns Visible field configurations.
   */
  visibleFields() {
    return this.config.fields.filter((field) => this.shouldShowField(field));
  }

  /**
   * Applies role-based and production-specific visibility rules for a field.
   * @param field Field configuration.
   * @returns True when the field should render.
   */
  shouldShowField(field: any) {
    if (field.superadminOnly && !this.auth.hasRole('Superadmin')) return false;
    if (this.resource === 'production' && field.key === 'rejectionReason') return Number(this.formModel.rejectedQuantity || 0) > 0;
    return true;
  }

  /**
   * Builds the backend payload from visible fields and resource-specific cleanup rules.
   * @returns Request payload for create/update.
   */
  private payloadForSave() {
    const payload: any = {};
    if (this.formModel._id) payload._id = this.formModel._id;
    for (const field of this.visibleFields()) {
      payload[field.key] = this.formModel[field.key];
      if (typeof payload[field.key] === 'string') {
        payload[field.key] = field.validation?.kind === 'email' ? normalizeText(payload[field.key], true) : sanitizeText(payload[field.key]);
      }
      if (field.type === 'date' && payload[field.key] instanceof Date) {
        payload[field.key] = this.toDateInput(payload[field.key]);
      }
      if (field.type === 'number' && hasValue(payload[field.key])) {
        payload[field.key] = Number(payload[field.key]);
      }
    }
    if (this.resource === 'production' && Number(this.formModel.rejectedQuantity || 0) <= 0) {
      payload.rejectionReason = '';
    }
    return payload;
  }

  private kindForField(field: FieldConfig): ValidationKind {
    if (field.relation) return 'select';
    if (field.type === 'number') return 'number';
    if (field.type === 'date') return 'date';
    if (field.type === 'select') return 'select';
    if (field.type === 'textarea') return 'textarea';
    return 'text';
  }
}
